import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  type LedgerDrawerProps,
  useOpenLedgerDrawer,
} from '@features/patient-ledger';
import { openLedgerDrawer } from '@features/patient-ledger/store/slice';
import { useUserState } from '@features/user';
import { usePatientById } from '@hooks/usePatientById';
import { useEventTracking, useSessionReplayCapture } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';

import { useStripeConnect } from '../store/hooks';
import type { PatientPaymentMethod } from '../types';

export interface RequestPaymentOptions {
  amount?: string;
  patientId?: string;
  patientName?: string;
  reason?: string;
  dueDate?: string;
  appointmentId?: string;
  encounterId?: string;
  actionId?: string;
  source?: string;
  patientTelephone?: string;
  hasPaymentMethod?: boolean;
  defaultPaymentMethod?: PatientPaymentMethod;
  paymentMethods?: PatientPaymentMethod[];
  onSuccess?: () => void;
}

interface RequestPaymentState {
  isLoading: boolean;
  canRequest: boolean;
  triggerPaymentRequest: () => void;
}

/**
 * Headless hook for requesting payments from patients.
 *
 * Pre-flight: SMS onboarding + Stripe Connect must both be set up. Once
 * those are clear, opens the unified Patient Ledger Drawer pre-targeting
 * the invoice flow (settle sheet, method=invoice). The drawer is the same
 * surface that the patient billing panel and encounter detail open — so
 * "Request payment" lands in the user's existing mental model.
 *
 * Several legacy options (defaultPaymentMethod, paymentMethods,
 * patientTelephone, dueDate, actionId) are dropped. The settle sheet
 * collects mobile override + reason itself, and uses the patient's default
 * card when method=card_on_file.
 */
export function useRequestPayment(options: RequestPaymentOptions = {}): RequestPaymentState {
  const {
    amount,
    patientId,
    patientName,
    encounterId,
    source = 'unknown',
    patientTelephone,
  } = options;

  const { open: showModal } = useModal();
  const openDrawer = useOpenLedgerDrawer();
  const { hasStripeAccount } = useStripeConnect();
  const { startCapture } = useSessionReplayCapture();
  const { payment, smsOnboarding, stripeConnect } = useEventTracking();
  const userState = useUserState();
  const location = useLocation();
  const navigate = useNavigate();
  // TODO: when this gets moved to a query hook, we can skip if telephone and name are provided
  const { patient } = usePatientById(patientId);
  const patientNameFinal =
    patientName || (patient ? `${patient.first_name} ${patient.last_name}` : undefined);

  const isSMSOnboarded = userState?.onboardingSteps.patient_communication_sms_onboarding_completed;
  const canRequest = true;

  // Convert legacy `amount` (dollars-as-string) into cents for the drawer.
  const defaultAmountCents = amount ? Math.round(parseFloat(amount) * 100) : undefined;

  const buildLedgerProps = (): LedgerDrawerProps => ({
    patientId: patientId ?? '',
    patientName: patientNameFinal ?? null,
    encounterId,
    defaultAmountCents,
    initialTab: 'ledger',
    initialAction: { kind: 'settle', method: 'invoice' },
    source,
  });

  const triggerPaymentRequest = useCallback(() => {
    startCapture('payment_request_triggered');
    payment.requestInitiated({
      source,
      amount: amount ? parseFloat(amount) : undefined,
    });

    if (!isSMSOnboarded) {
      smsOnboarding.initiated({ source: 'payment_request' });
      showModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {
        // After SMS onboarding completes, either continue into Stripe
        // Connect (still a real modal) or fire the slice action to open
        // the ledger drawer. The continuation is dispatched as a Redux
        // action by the onboarding modal — slice action types match
        // their dispatched form so this works without a wrapper.
        continuationAction: !hasStripeAccount
          ? {
              type: 'modals/pushModal',
              payload: {
                modalType: MODAL_TYPES.STRIPE_CONNECT_INFO,
                modalProps: {
                  returnUrl: `${window.location.href}${window.location.search ? '&' : '?'}openPaymentRequest=true`,
                },
              },
            }
          : {
              type: openLedgerDrawer.type,
              payload: buildLedgerProps(),
            },
      });
      return;
    }

    if (!hasStripeAccount) {
      stripeConnect.initiated({ source: 'payment_request' });
      showModal(MODAL_TYPES.STRIPE_CONNECT_INFO, {
        returnUrl: `${window.location.href}${window.location.search ? '&' : '?'}openPaymentRequest=true`,
      });
      return;
    }

    payment.modalOpened({ source });
    openDrawer(buildLedgerProps());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSMSOnboarded,
    hasStripeAccount,
    amount,
    patientId,
    patientNameFinal,
    encounterId,
    source,
    showModal,
    openDrawer,
    startCapture,
    payment,
    smsOnboarding,
    stripeConnect,
  ]);

  // Auto-open after Stripe Connect onboarding redirect.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenPaymentRequest = params.get('openPaymentRequest') === 'true';

    if (shouldOpenPaymentRequest && hasStripeAccount && isSMSOnboarded) {
      stripeConnect.completed({});
      payment.modalOpened({ source: 'stripe_return' });
      openDrawer(buildLedgerProps());

      params.delete('openPaymentRequest');
      const newSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
          hash: location.hash,
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, openDrawer, hasStripeAccount, isSMSOnboarded]);

  // Suppress unused-warning on legacy optional props that no longer affect behavior.
  void patientTelephone;

  return {
    isLoading: false,
    canRequest,
    triggerPaymentRequest,
  };
}
